import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { Loader2, X, XCircle } from 'lucide-react';

import { authenticationAtom } from '../State';
import { storageAPI } from '../StorageAPI';
import FileTreePicker, {
    emptyPickerSelection,
    PickerSelection,
} from '../real-gain/prompt-tool/FileTreePicker';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { DEFAULT_VOLUME_KEY } from '../StorageAPI';

export interface StoragePickResult {
    /** ManagedFile ids of the individually picked files. */
    fileIds: string[];
    /**
     * The picked folders — their contents are resolved server-side. Each carries the volume it was
     * browsed in, because a selection can span volumes and the server walks each folder against the
     * volume it actually lives in.
     */
    folderPaths: Array<{ path: string; volume: string }>;
}

interface StorageFilePickerDialogProps {
    hidden: boolean;
    onDismiss: () => void;
    onConfirm: (picked: StoragePickResult) => void;
}

/**
 * Pick files and folders out of the customer's managed file system.
 *
 * Registration lives here rather than in the tree: a file that exists only in the storage backend
 * (a SharePoint file created in the site, never uploaded through the platform) has no ManagedFile
 * document and therefore no id to hand over. Doing it on confirm — instead of on every checkbox —
 * means a file the user checks and unchecks again never materializes a record. Picked *folders* are
 * passed on as paths; the server walks them and registers what it finds there.
 */
export default function StorageFilePickerDialog({
    hidden,
    onDismiss,
    onConfirm,
}: StorageFilePickerDialogProps) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const [selection, setSelection] = useState<PickerSelection>(emptyPickerSelection);
    const [isResolving, setIsResolving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fileCount = selection.files.length;
    const folderCount = selection.folders.length;
    const selectedCount = fileCount + folderCount;

    // "2 Dateien, 1 Ordner" — counted apart rather than summed. A folder is not a file, and it
    // stands for however many files sit below it (resolved server-side, so the number is not known
    // here); adding the two together showed "Hinzufügen (1)" for a folder holding dozens, which
    // read as "only one file is coming along" (Dr. Sasse, 2026-09-04).
    const selectionSummary = [
        fileCount > 0 ? t('chatExchange.selectedFilesCount', { count: fileCount }) : null,
        folderCount > 0 ? t('chatExchange.selectedFoldersCount', { count: folderCount }) : null,
    ]
        .filter(Boolean)
        .join(', ');

    const close = () => {
        setSelection(emptyPickerSelection);
        setError(null);
        onDismiss();
    };

    const confirm = async () => {
        setIsResolving(true);
        setError(null);
        try {
            const fileIds = await Promise.all(
                selection.files.map(async file => {
                    if (file._id) {
                        return file._id;
                    }
                    // Register into the volume the file was browsed in, not the default one — that
                    // is what puts the right `volume` on the new ManagedFile record.
                    const registered = await storageAPI.registerFile(
                        authentication.customer,
                        file.volumeKey || DEFAULT_VOLUME_KEY,
                        file.path,
                        { providerId: file.providerId, name: file.name, webUrl: file.webUrl }
                    );
                    return registered?._id ? String(registered._id) : null;
                })
            );

            onConfirm({
                fileIds: fileIds.filter((id): id is string => Boolean(id)),
                folderPaths: selection.folders.map(folder => ({
                    path: folder.path,
                    volume: folder.volumeKey || DEFAULT_VOLUME_KEY,
                })),
            });
            setSelection(emptyPickerSelection);
            onDismiss();
        } catch (err: any) {
            console.error('Failed to resolve the picked files:', err);
            setError(t('chatExchange.storagePickFailed'));
        } finally {
            setIsResolving(false);
        }
    };

    return (
        <Dialog
            open={!hidden}
            onOpenChange={next => {
                if (!next) close();
            }}
        >
            <DialogContent
                data-testid="chat-storage-picker"
                className="max-w-[min(720px,96vw)]"
                // Der Auswahldialog schließt nur über „Abbrechen“ bzw. das
                // Schließkreuz — wie zuvor mit Fluents `isBlocking`.
                onInteractOutside={event => event.preventDefault()}
                onEscapeKeyDown={event => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('chatExchange.pickFromStorageTitle')}</DialogTitle>
                    <DialogDescription>
                        {t('chatExchange.pickFromStorageSubText')}
                    </DialogDescription>
                </DialogHeader>
                {error && (
                    <Alert variant="destructive" className="flex items-start gap-3 pr-10">
                        <XCircle className="h-4 w-4" aria-hidden />
                        <AlertDescription className="flex-1">{error}</AlertDescription>
                        <button
                            type="button"
                            onClick={() => setError(null)}
                            aria-label={t('ui.close')}
                            data-testid="chat-storage-picker-error-dismiss"
                            className="absolute right-2 top-2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
                        >
                            <X className="h-4 w-4" aria-hidden />
                        </button>
                    </Alert>
                )}
                <FileTreePicker
                    selection={selection}
                    onSelectionChange={setSelection}
                    selectFolders
                />
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        data-testid="chat-storage-picker-cancel"
                        onClick={close}
                    >
                        {t('ui.cancel')}
                    </Button>
                    <Button
                        type="button"
                        data-testid="chat-storage-picker-confirm"
                        onClick={confirm}
                        disabled={selectedCount === 0 || isResolving}
                    >
                        {isResolving && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                        )}
                        {selectedCount > 0
                            ? t('chatExchange.addSelection', { summary: selectionSummary })
                            : t('chatExchange.addFiles')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
